import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@shared/ui/card';
import { Badge } from '@shared/ui/badge';
import { Button } from '@shared/ui/button';
import { Tabs } from '@shared/ui/tabs';
import { GenericTable } from '@shared/components/GenericTable';

import { 
  ArrowLeft, 
  FileText, 
  User, 
  Phone, 
  Mail, 
  Wrench,
  Clock,
  DollarSign,
  AlertCircle,
  ExternalLink,
  Loader2
} from 'lucide-react';
import ServiceOrderBanner from './serviceOrderBanner';
import { useDownloadReport, useRejectedSODetail } from '@features/wx/dispatcher/hooks';
import { useNavigate, useParams } from 'react-router-dom';
import { useCurrency, useRemoteUtilityId } from '@shared/selectors/globalSelectors';
import { ServiceOrderDetailsProps } from '@features/wx/dispatcher/types';
import { FieldTooltip, Tooltip, TooltipTrigger } from '@shared/ui/tooltip';



const ServiceOrderDetails = ({ order, status, onBack }: ServiceOrderDetailsProps) => {
  const {id}=useParams();
  const currency=useCurrency();
  const navigate = useNavigate();
  const remoteUtilityId=useRemoteUtilityId();
  const {data:rejectedSODetail}=useRejectedSODetail({remote_utility_id:remoteUtilityId,id:id})
  
  const data = rejectedSODetail?.result;
    const downloadCSV = useDownloadReport();
const handleDownload = () => {
  if (!id) return;
  downloadCSV.mutateAsync({
    reportId: Number(id),
    tabName: "rejected",
  });
};

  // Render document link helper
  const renderDocumentLink = (document: any) => {
    if (document.file) {
      return (
        <a
          href={document.file}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center text-blue-600 hover:text-blue-800"
        >
          <FileText className="h-4 w-4 mr-1" />
          View File
          <ExternalLink className="h-3 w-3 ml-1" />
        </a>
      );
    }

    if (document.link) {
      return (
        <a
          href={document.link}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center text-blue-600 hover:text-blue-800"
        >
          <ExternalLink className="h-4 w-4 mr-1" />
          Open Link
          <ExternalLink className="h-3 w-3 ml-1" />
        </a>
      );
    }

    return <span className="text-gray-500">No link/file</span>;
  };

  // Table columns
  const tasksColumns = [
    {
      key: "name",
      header: "Task Name",
      render: (value: string) => (
        <span className="font-medium text-gray-900">{value || 'N/A'}</span>
      ),
    },
    {
      key: "time",
      header: "TAT (Mins)",
      render: (value: number) => <span className="text-gray-700">{value || 'N/A'}</span>,
    },
    {
      key: "downtime",
      header: "Downtime (Mins)",
      render: (value: number) => (
        <span className={value > 0 ? "text-orange-600" : "text-gray-700"}>
          {value || 'N/A'}
        </span>
      ),
    },
    {
      key: "isPhotoReq",
      header: "Photo Required",
      type: "badge" as const,
      badgeConfig: {
        getClassName: (value: boolean) =>
          value
            ? "bg-green-100 text-green-800"
            : "bg-gray-100 text-gray-800",
      },
    },
  ];

  const materialsColumns = [
    {
      key: "name",
      header: "Name",
      render: (value: string) => (
        <span className="font-medium text-gray-900">{value || 'N/A'}</span>
      ),
    },
    {
      key: "size",
      header: "Size",
      render: (value: string) => <span className="text-gray-700">{value || 'N/A'}</span>,
    },
    {
      key: "quantityRequired",
      header: "Quantity",
      render: (value: number) => <span className="text-gray-700">{value || 'N/A'}</span>,
    },
    {
      key: "cost",
      header: "Cost",
      render: (value: number) => (
        <span className="font-semibold text-green-600">
        {currency}  {value ? `${value}` : 'N/A'}
        </span>
      ),
    },
  ];

  const readingsColumns = [
    {
      key: "name",
      header: "Name",
      render: (value: string) => (
        <span className="font-medium text-gray-900">{value || 'N/A'}</span>
      ),
    },
    {
      key: "unit",
      header: "Unit",
      render: (value: string) => <span className="text-gray-700">{value || 'N/A'}</span>,
    },
{
  key: "range",
  header: "Range",
  render: (value: string, row: any) => {
    const min = row.minimum || 'N/A';
    const max = row.maximum || 'N/A';
    return <span className="text-gray-700">{`${min} - ${max}`}</span>;
  },
},
    {
      key: "isPhotoReq",
      header: "Photo Required",
      type: "badge" as const,
      badgeConfig: {
        getClassName: (value: boolean) =>
          value
            ? "bg-green-100 text-green-800"
            : "bg-gray-100 text-gray-800",
      },

    },
  ];

  const helpResourcesColumns = [
    {
      key: "typeDisplay",
      header: "Resource Type",
      type: "badge" as const,
      badgeConfig: {
        getClassName: (value: string) => {
          switch (value) {
            case "Document":
            case "instruction":
              return "bg-blue-100 text-blue-800";
            case "Video Link":
              return "bg-red-100 text-red-800";
            case "Checklist":
              return "bg-green-100 text-green-800";
            case "Hotline":
              return "bg-purple-100 text-purple-800";
            default:
              return "bg-gray-100 text-gray-800";
          }
        },
      },
    },
    {
      key: "description",
      header: "Description",
      render: (value: string) => (
        <span className="text-gray-900">{value || 'N/A'}</span>
      ),
    },
    {
      key: "file",
      header: "Access",
      render: (_value: any, row: any) => renderDocumentLink(row),
    },
  ];



  const getStatusColor = () => {
    if (status === 'rejected') return 'text-red-600';
    if (status === 'rework') return 'text-blue-600';
    return 'text-orange-600';
  };

  const getHeaderTitle = () => {
    if (status === 'rejected') return `Rejected Service Order - ${data?.sop?.name || 'N/A'}`;
    if (status === 'rework') return `Rework Service Order - ${data?.sop?.name || 'N/A'}`;
    return `Rejected Service Order - ${data?.sop?.name || 'N/A'}`;
  };

  // Consumer Details Content
  const ConsumerDetailsContent = () => (
     <div className="grid grid-cols-2 gap-6 mt-4">  

      <div>
        <h3 className="font-medium mb-3 flex items-center gap-2">
          <User className="h-4 w-4" />
          Consumer Details
        </h3>
        <div className="space-y-2">
          <div>
            <p className="text-sm text-gray-600">Consumer Name</p>
            <p className="font-medium">{data?.requestUsername || 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Account No</p>
            <p className="font-medium">{data?.requestAccountNo || 'N/A'}</p>
          </div>
          <div className="flex items-center gap-2">
            <Phone className="h-4 w-4 text-gray-400" />
            <p className="font-medium">{data?.mobileNumber || 'N/A'}</p>
          </div>
        </div>
      </div>

      <div>
        <h3 className="font-medium mb-3 flex items-center gap-2">
          <Wrench className="h-4 w-4" />
          Meter Details
        </h3>
        <div className="space-y-2">
          <div>
            <p className="text-sm text-gray-600">Meter No</p>
            <p className="font-medium">{data?.meterInfo?.meterNumber || 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Device No</p>
            <p className="font-medium">{data?.meterInfo?.deviceNo || 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Premise</p>
            <p className="font-medium">{data?.meterInfo?.territoryData?.premise || 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Physical Status</p>
            <p className="font-medium">{data?.meterInfo?.physicalStatusDisplay || 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Assignment Status</p>
            <p className="font-medium">{data?.meterInfo?.statusDisplay || 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Reading Status</p>
            <p className="font-medium">{data?.meterInfo?.readingStatusDisplay || 'N/A'}</p>
          </div>
        </div>
      </div>
    </div>
  );

  // Assignee Details Content
  const AssigneeDetailsContent = () => (
    <div className="space-y-4 mt-4">
      <div>
        <h3 className="font-medium  mb-3">Assignee Details</h3>
        <div className="space-y-2">
          <div>
            <p className="text-sm text-gray-600">Technician Name</p>
            <p className="font-medium">{data?.fieldForce?.name || 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">FID</p>
            <p className="font-medium">{data?.fieldForce?.fieldForceNo || 'N/A'}</p>
          </div>
          <div className="flex items-center gap-2">
            <Phone className="h-4 w-4 text-gray-400" />
            <p className="font-medium">{data?.fieldForce?.contactNumber || 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Role</p>
            <p className="font-medium">{data?.fieldForce?.role || 'N/A'}</p>
          </div>
          <div className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-gray-400" />
            <p className="font-medium">{data?.fieldForce?.email || 'N/A'}</p>
          </div>
        </div>
      </div>
    </div>
  );

  // Consumer & Meter Tabs Configuration
  const consumerMeterTabComponents = {
    'consumer-details': {
      label: 'Consumer & Meter Details',
      icon: <User className="h-4 w-4" />,
      component: <ConsumerDetailsContent />,
    },
    'assigned-technician': {
      label: 'Assigned Technician',
      icon: <User className="h-4 w-4" />,
      component: <AssigneeDetailsContent />,
    },
  };

  // Job Requirements Tab Content Components
  const OverviewContent = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
    
<div>
  <label className="text-sm font-medium text-gray-500">
    Description
  </label>
  {data?.sop?.description ? (
    data.sop.description.length > 20 ? (
      <Tooltip>
        <TooltipTrigger asChild>
          <p className="text-base text-gray-900 mt-1 cursor-pointer">
            {data.sop.description.slice(0, 20)}...
          </p>
        </TooltipTrigger>
        <FieldTooltip>{data.sop.description}</FieldTooltip>
      </Tooltip>
    ) : (
      <p className="text-base text-gray-900 mt-1">
        {data.sop.description}
      </p>
    )
  ) : (
    <p className="text-base text-gray-900 mt-1">N/A</p>
  )}
</div>
      <div>
        <label className="text-sm font-medium text-gray-500">
          Required Field Force
        </label>
        <p className="text-base text-gray-900 mt-1">
          {data?.sop?.staffRequired ? `${data.sop.staffRequired} Technician${data.sop.staffRequired > 1 ? 's' : ''}` : 'N/A'}
        </p>
      </div>
      <div>
        <label className="text-sm font-medium text-gray-500">
          Estimated Efforts
        </label>
        <p className="text-base text-gray-900 mt-1">
          {data?.sop?.taskTimeCount ? `${data.sop.taskTimeCount}h` : 'N/A'}
        </p>
      </div>
      <div>
        <label className="text-sm font-medium text-gray-500">
          Downtime
        </label>
        <p className="text-base text-gray-900 mt-1">
          {data?.actualDowntime !== null && data?.actualDowntime !== undefined ? `${data.actualDowntime} mins` : 'N/A'}
        </p>
      </div>
    </div>
  );

  const TasksContent = () => {
    const tasks = data?.sop?.task || [];
    return (
      <GenericTable
        data={tasks}
        columns={tasksColumns}
        rowKey={(row: any) => row.id || row.taskId || Math.random().toString()}
      />
    );
  };

  const MaterialsContent = () => {
    const materials = data?.sop?.material || [];
    return (
      <GenericTable
        data={materials}
        columns={materialsColumns}
        rowKey={(row: any) => row.id || row.materialId || Math.random().toString()}
      />
    );
  };

  const ReadingsContent = () => {
    const readings = data?.sop?.readings || [];
    return (
      <GenericTable
        data={readings}
        columns={readingsColumns}
        rowKey={(row: any) => row.id || row.readingId || Math.random().toString()}
      />
    );
  };

  const HelpContent = () => {
    const helpResources = data?.sop?.documents || [];
    return (
      <GenericTable
        data={helpResources}
        columns={helpResourcesColumns}
        rowKey={(row: any) => row.id?.toString() || Math.random().toString()}
      />
    );
  };

  // Job Requirements Tabs Configuration
  const jobRequirementsTabComponents = {
    'overview': {
      label: 'Overview',
      icon: <DollarSign className="h-4 w-4" />,
      component: <OverviewContent />,
    },
    'tasks': {
      label: 'Tasks',
      icon: <FileText className="h-4 w-4" />,
      count: data?.sop?.task?.length || 0,
      component: <TasksContent />,
    },
    'materials': {
      label: 'Materials',
      icon: <Wrench className="h-4 w-4" />,
      count: data?.sop?.material?.length || 0,
      component: <MaterialsContent />,
    },
    'readings': {
      label: 'Readings',
      icon: <FileText className="h-4 w-4" />,
      count: data?.sop?.readings?.length || 0,
      component: <ReadingsContent />,
    },
    'help': {
      label: 'Help',
      icon: <AlertCircle className="h-4 w-4" />,
      count: data?.sop?.documents?.length || 0,
      component: <HelpContent />,
    },
  };
const handleBack = () => {
  if (onBack) {
    onBack();
  } else {
    navigate(-1);
  }
};
  return (
    <div className="w-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <h1 className="text-2xl font-bold">
            {getHeaderTitle()}
          </h1>
        </div>
        <div className="flex items-center gap-3">
         <Button 
  className="bg-blue-600 hover:bg-blue-700 text-white"
  onClick={handleDownload}
  disabled={downloadCSV.isPending}
>
  {downloadCSV.isPending ? (
    <>
      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
      Downloading...
    </>
  ) : (
    <>
      <FileText className="h-4 w-4 mr-2" />
      View Report
    </>
  )}
</Button>
          <Button variant="outline" onClick={handleBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        </div>
      </div>

      {/* Parent-Child Linking Banner */}
{/* <ServiceOrderBanner 
  type={data?.statusDisplay === 'Rework Assigned' ? 'child' : 'parent'} 
  order={{
    id: data?.id?.toString() || '',
    reworkSO: data?.reworkOrders?.jobCardNo || null,
    rejectionReason: data?.rejectionReasonDisplay || '',
    rejectionComments: data?.remark || '',
    parentOrderId: data?.jobCardNo|| ''
  }}
  reworkOrders={data?.reworkOrders}
/> */}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card className="border-l-4 border-l-green-500">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-green-600" />
              <CardTitle className="text-sm font-medium text-gray-600">Total Cost</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
             {currency} {data?.totalActualCost || 'N/A'}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Actual cost for completed work
            </p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-blue-500">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-blue-600" />
              <CardTitle className="text-sm font-medium text-gray-600">Total Time</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              {data?.totalInterval || 'N/A'}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Time spent on work completion
            </p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-orange-500">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-orange-600" />
              <CardTitle className="text-sm font-medium text-gray-600">Status</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${getStatusColor()}`}>
              {data?.statusDisplay || 'N/A'}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Service order status
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Service Order Details */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Service Order Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600 mb-1">SO Name</p>
                <p className="font-medium">{data?.sop?.name || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Short Name</p>
                <p className="font-medium">{data?.sop?.shortName || 'N/A'}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600 mb-1">Created On</p>
                <p className="font-medium">{data?.createdDate || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Assigned On</p>
                <p className="font-medium">{data?.assignDate || 'N/A'}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600 mb-1">Expected Completion</p>
                <p className="font-medium">{data?.expectedEndDate || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Utility Services</p>
                <p className="font-medium">{data?.sop?.utilityServices?.join(', ') || 'N/A'}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600 mb-1">Area</p>
                <p className="font-medium">{data?.areaName || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Subarea</p>
                <p className="font-medium">{data?.subAreaName || 'N/A'}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600 mb-1">Premise</p>
                <p className="font-medium">{data?.premiseName || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Tax Rate</p>
                <p className="font-medium">{data?.taxRate ? `${data.taxRate}%` : 'N/A'}</p>
              </div>
            </div>
<div>
  <p className="text-sm text-gray-600 mb-1">Description</p>
  {data?.sop?.description ? (
    data.sop.description.length > 70 ? (
      <Tooltip>
        <TooltipTrigger asChild>
          <p className="font-medium cursor-pointer">
            {data.sop.description.slice(0, 70)}...
          </p>
        </TooltipTrigger>
        <FieldTooltip>{data.sop.description}</FieldTooltip>
      </Tooltip>
    ) : (
      <p className="font-medium">{data.sop.description}</p>
    )
  ) : (
    <p className="font-medium">N/A</p>
  )}
</div>
<div>
  <p className="text-sm text-gray-600 mb-1">Remark</p>
  {data?.remark ? (
    data.remark.length > 70 ? (
      <Tooltip>
        <TooltipTrigger asChild>
          <p className="font-medium cursor-pointer">
            {data.remark.slice(0, 70)}...
          </p>
        </TooltipTrigger>
        <FieldTooltip>{data.remark}</FieldTooltip>
      </Tooltip>
    ) : (
      <p className="font-medium">{data.remark}</p>
    )
  ) : (
    <p className="font-medium">No remarks</p>
  )}
</div>
          </CardContent>
        </Card>

        {/* Consumer & Meter Details */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Consumer & Meter Details</CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs
              tabComponents={consumerMeterTabComponents}
              defaultValue="consumer-details"
              urlMapping={{
                'consumer-details': 'consumer',
                'assigned-technician': 'technician',
              }}
              tabsListClassName="w-full grid grid-cols-2 bg-muted/30 p-1"
              idPrefix="consumer-meter-tab"
              level={1}
            />
          </CardContent>
        </Card>
      </div>

      {/* Job Requirements & Reports */}
      <Card className="mt-8">
        <CardHeader>
          <CardTitle className="text-lg font-semibold">Job Requirements & Reports</CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs
            tabComponents={jobRequirementsTabComponents}
            defaultValue="overview"
            urlMapping={{
              'overview': 'overview',
              'tasks': 'tasks',
              'materials': 'materials',
              'readings': 'readings',
              'help': 'help',
            }}
            tabsListClassName="w-full grid grid-cols-5 bg-muted/30 p-1"
            idPrefix="job-requirements-tab"
            level={2}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default ServiceOrderDetails;